import React, { useState } from 'react';
import { apiFetch, setApiAccessToken } from './apiClient';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import PublicAuthShell from './components/PublicAuthShell';
import { useLanguage } from './i18n/LanguageContext';
import './LoginPage.css';

const LoginPage = ({
  onLogin,
  onShowRegister,
  onGoHome,
  embedded = false,
  hideSwitch = false,
}) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authView, setAuthView] = useState('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotStep, setForgotStep] = useState('request');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotErr, setForgotErr] = useState('');
  const forgotStepDetails = {
    request: {
      title: 'Step 1 of 3: Request code',
      description: 'Enter your account email and we will send a 6-digit verification code to that inbox.',
    },
    verify: {
      title: 'Step 2 of 3: Verify code',
      description: 'Check your email for the 6-digit code, then enter it below to continue.',
    },
    reset: {
      title: 'Step 3 of 3: Set a new password',
      description: 'Create a new password and confirm it to finish resetting your account.',
    },
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter email or phone number and password.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch('/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Login failed');
        setLoading(false);
        return;
      }
      const data = await res.json();
      setLoading(false);
      if (data.token) {
        setApiAccessToken(data.token);
      }
      onLogin(data.user);
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  const handleSendResetCode = async () => {
    setForgotErr('');
    setForgotMsg('');
    if (!forgotEmail.trim()) {
      setForgotErr('Enter your email first.');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await apiFetch('/password-reset/send-code', {
        method: 'POST',
        body: JSON.stringify({
          email: forgotEmail.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setForgotErr(data.message || 'Could not submit reset request.');
        setForgotMsg('');
      } else if (data.cooldown) {
        setForgotErr(data.message || 'Please wait before requesting another code.');
        setForgotMsg('');
      } else if (data.codeSent) {
        setForgotMsg(
          data.message ||
            'A 6-digit verification code was sent to your email. Check your inbox and Spam folder.'
        );
        setForgotErr('');
        if (forgotStep === 'request') {
          setForgotStep('verify');
        }
      } else {
        setForgotMsg(
          data.message ||
            'If this email is registered, a verification code will be sent. Check the address and try again.'
        );
        setForgotErr('');
      }
    } catch {
      setForgotErr('Network error. Try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyResetCode = async () => {
    setForgotErr('');
    setForgotMsg('');
    if (!forgotEmail.trim()) {
      setForgotErr('Enter your email first.');
      setForgotStep('request');
      return;
    }
    if (!forgotCode.trim()) {
      setForgotErr('Enter the 6-digit code sent to your email.');
      return;
    }
    if (!/^\d{6}$/.test(forgotCode.trim())) {
      setForgotErr('Verification code must be exactly 6 digits.');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await apiFetch('/password-reset/verify-code', {
        method: 'POST',
        body: JSON.stringify({
          email: forgotEmail.trim(),
          code: forgotCode.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setForgotErr(data.message || 'Code verification failed.');
      } else {
        setForgotMsg(data.message || 'Code verified.');
        setForgotStep('reset');
      }
    } catch {
      setForgotErr('Network error. Try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setForgotErr('');
    setForgotMsg('');
    if (forgotStep !== 'reset') {
      setForgotErr('Verify your 6-digit email code before setting a new password.');
      return;
    }
    if (!/^\d{6}$/.test(forgotCode.trim())) {
      setForgotErr('Verification code must be exactly 6 digits.');
      setForgotStep('verify');
      return;
    }
    if (!resetPassword || !resetConfirm) {
      setForgotErr('Enter new password and confirm it.');
      return;
    }
    if (resetPassword !== resetConfirm) {
      setForgotErr('Password and confirmation do not match.');
      return;
    }
    setForgotLoading(true);
    try {
      const res = await apiFetch('/password-reset/reset', {
        method: 'POST',
        body: JSON.stringify({
          email: forgotEmail.trim(),
          code: forgotCode.trim(),
          newPassword: resetPassword,
          newPassword_confirmation: resetConfirm,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setForgotErr(data.message || 'Could not reset password.');
      } else {
        setForgotMsg(data.message || 'Password reset successful.');
        setResetPassword('');
        setResetConfirm('');
        setForgotCode('');
        setTimeout(() => {
          setAuthView('login');
        }, 1000);
      }
    } catch {
      setForgotErr('Network error. Try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <PublicAuthShell
      active="login"
      embedded={embedded}
      showNav={authView !== 'login'}
      authTheme
      hideScrollProgress={authView === 'login'}
      centerVertically={authView === 'login'}
      onHome={onGoHome}
      onLogin={() => {}}
      onRegister={onShowRegister}
    >
      <div
        className={`public-auth-card login-ag-card${
          authView === 'forgot' ? ' login-ag-card--forgot' : ' login-ag-card--signin'
        }`}
      >
        {authView === 'login' ? (
          <form className="login-form login-ag-form login-ag-form--signin" onSubmit={handleSubmit}>
            <header className="login-ag-header">
              <div className="login-ag-logo-wrap auth-animate">
                <img src="/branding/cb-app-logo-circle.svg" alt="" className="login-ag-logo" />
              </div>
              <h1 className="login-ag-brand auth-animate auth-animate--d1">
                <span className="login-ag-brand-main">cbonline</span>
                <span className="login-ag-brand-sub">app</span>
              </h1>
              <p className="login-ag-subtitle auth-animate auth-animate--d2">{t('auth.signInTitle')}</p>
            </header>

            {error ? <div className="error login-ag-error auth-animate auth-animate--d3">{error}</div> : null}

            <div className="login-ag-field auth-animate auth-animate--d3">
              <label htmlFor="login-email" className="login-ag-label">
                {t('auth.email')}
              </label>
              <input
                id="login-email"
                type="text"
                inputMode="email"
                placeholder="you@company.com or 0712 345 678"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="login-ag-input"
                autoComplete="username"
              />
              <p className="login-ag-field-hint">{t('auth.emailHint')}</p>
            </div>

            <div className="login-ag-field auth-animate auth-animate--d4">
              <div className="login-ag-label-row">
                <label htmlFor="login-password" className="login-ag-label">
                  {t('auth.password')}
                </label>
                <a
                  href="#forgot-password"
                  className="login-ag-forgot-link"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    setAuthView('forgot');
                    setForgotErr('');
                    setForgotMsg('');
                    setForgotStep('request');
                    setForgotCode('');
                    setResetPassword('');
                    setResetConfirm('');
                    setShowResetPassword(false);
                    if (!forgotEmail && email) setForgotEmail(email);
                  }}
                >
                  {t('auth.forgotPassword')}
                </a>
              </div>
              <div className="login-ag-password-wrap">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="login-ag-input login-ag-input--password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-ag-eye"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <LuEyeOff size={22} strokeWidth={1.25} aria-hidden />
                  ) : (
                    <LuEye size={22} strokeWidth={1.25} aria-hidden />
                  )}
                </button>
              </div>
            </div>

            <button type="submit" className="login-ag-submit auth-animate auth-animate--d5" disabled={loading}>
              {loading ? <span className="login-ag-spinner" aria-hidden /> : t('auth.signIn')}
            </button>

            {!hideSwitch && typeof onShowRegister === 'function' ? (
              <p className="login-ag-signup auth-animate auth-animate--d6">
                {t('auth.noAccount')}{' '}
                <a
                  href="#register"
                  className="login-ag-signup__link"
                  onClick={(e) => {
                    e.preventDefault();
                    onShowRegister();
                  }}
                >
                  {t('auth.signUp')}
                </a>
              </p>
            ) : null}
          </form>
        ) : (
          <div className="login-ag-form login-ag-form--forgot" key={forgotStep}>
            <header className="login-ag-header login-ag-header--compact">
              <h1 className="login-ag-title auth-animate">Reset password</h1>
              <p key={forgotStep} className="login-ag-lead auth-animate--swap">
                {forgotStepDetails[forgotStep].description}
              </p>
            </header>

            <ol className="login-ag-steps auth-animate auth-animate--d2" aria-label="Reset steps">
              {['request', 'verify', 'reset'].map((step, i) => (
                <li
                  key={step}
                  className={`login-ag-steps__item${
                    forgotStep === step
                      ? ' login-ag-steps__item--active'
                      : ['request', 'verify', 'reset'].indexOf(forgotStep) > i
                        ? ' login-ag-steps__item--done'
                        : ''
                  }`}
                >
                  <span className="login-ag-steps__num">{i + 1}</span>
                  <span className="login-ag-steps__label">
                    {step === 'request' ? 'Email' : step === 'verify' ? 'Code' : 'New password'}
                  </span>
                </li>
              ))}
            </ol>

            {forgotErr ? <div className="error login-ag-error auth-animate--swap">{forgotErr}</div> : null}
            {forgotMsg ? <div className="login-ag-success auth-animate--swap">{forgotMsg}</div> : null}

            <div className="login-ag-field auth-animate auth-animate--d3">
              <label htmlFor="forgot-email" className="login-ag-label">Account email</label>
              <input
                id="forgot-email"
                type="email"
                className="login-ag-input"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                autoComplete="email"
                disabled={forgotStep !== 'request'}
              />
            </div>

            {forgotStep === 'verify' ? (
              <div className="login-ag-field auth-animate--swap">
                <label htmlFor="forgot-code" className="login-ag-label">Verification code</label>
                <input
                  id="forgot-code"
                  type="text"
                  className="login-ag-input register-ag-code"
                  placeholder="Enter 6-digit code"
                  value={forgotCode}
                  onChange={(e) => setForgotCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                />
              </div>
            ) : null}

            {forgotStep === 'reset' ? (
              <>
                <p className="login-ag-step-hint auth-animate--swap">
                  Email verified for <strong>{forgotEmail.trim()}</strong>
                </p>
                <div className="login-ag-field auth-animate--swap">
                  <label htmlFor="reset-password-new" className="login-ag-label">New password</label>
                  <div className="login-ag-password-wrap">
                    <input
                      id="reset-password-new"
                      type={showResetPassword ? 'text' : 'password'}
                      className="login-ag-input login-ag-input--password"
                      placeholder="Enter new password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-ag-eye"
                      onClick={() => setShowResetPassword((v) => !v)}
                      aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                    >
                      {showResetPassword ? (
                        <LuEyeOff size={22} strokeWidth={1.25} aria-hidden />
                      ) : (
                        <LuEye size={22} strokeWidth={1.25} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>
                <div className="login-ag-field auth-animate--swap">
                  <label htmlFor="reset-password-confirm" className="login-ag-label">Confirm new password</label>
                  <div className="login-ag-password-wrap">
                    <input
                      id="reset-password-confirm"
                      type={showResetPassword ? 'text' : 'password'}
                      className="login-ag-input login-ag-input--password"
                      placeholder="Confirm new password"
                      value={resetConfirm}
                      onChange={(e) => setResetConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="login-ag-eye"
                      onClick={() => setShowResetPassword((v) => !v)}
                      aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                    >
                      {showResetPassword ? (
                        <LuEyeOff size={22} strokeWidth={1.25} aria-hidden />
                      ) : (
                        <LuEye size={22} strokeWidth={1.25} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>
              </>
            ) : null}

            <button
              type="button"
              className="login-ag-submit auth-animate auth-animate--d4"
              disabled={forgotLoading}
              onClick={() => {
                if (forgotStep === 'request') {
                  void handleSendResetCode();
                  return;
                }
                if (forgotStep === 'verify') {
                  void handleVerifyResetCode();
                  return;
                }
                void handleResetPassword();
              }}
            >
              {forgotLoading ? (
                <span className="login-ag-spinner" aria-hidden />
              ) : forgotStep === 'request' ? (
                'Send verification code'
              ) : forgotStep === 'verify' ? (
                'Verify code'
              ) : (
                'Reset password'
              )}
            </button>

            {forgotStep === 'verify' ? (
              <button
                type="button"
                className="login-ag-btn-ghost auth-animate auth-animate--d5"
                onClick={() => void handleSendResetCode()}
                disabled={forgotLoading}
              >
                Resend code
              </button>
            ) : null}

            <button
              type="button"
              className="login-ag-btn-ghost login-ag-btn-ghost--back auth-animate auth-animate--d6"
              onClick={() => {
                setAuthView('login');
                setForgotStep('request');
                setForgotErr('');
                setForgotMsg('');
                setForgotCode('');
                setResetPassword('');
                setResetConfirm('');
                setShowResetPassword(false);
              }}
            >
              ← Back to sign in
            </button>
          </div>
        )}
      </div>
    </PublicAuthShell>
  );
};

export default LoginPage;
